/**
 * Live-scan FRAME DUMP for the exported scan log (2026-09-23).
 *
 * Pokémon GO blocks screenshots on Android, so a device report of a missed
 * or phantom dex cell could never be replayed offline: the log said what the
 * classifier decided and nothing about what it saw. This keeps a bounded set
 * of the JPEG frames the scanner actually ingested (the exact bytes the OCR
 * and presence classifier read) and appends them to the Settings "Export scan
 * log" file as `[dex-scan] frame-dump` lines, one per frame, base64 JPEG.
 *
 * Which frames: every ingested frame that brought a dex number the session
 * had not read before (the first look at each new row) plus one sample every
 * SAMPLE_EVERY ingested frames (a still page the trainer dwelt on), capped at
 * FRAME_DUMP_MAX with the oldest dropped. At ~80 KB per 576x1280 q60 JPEG
 * that is ~4 MB of memory and export at the cap.
 *
 * Only recorded while the scan-log export is unlocked (the seven-tap unlock or
 * the permanent export accounts), so ordinary scans pay nothing. Pure
 * telemetry: no scan decision reads it.
 */
import { isScanLogExportUnlocked } from "./scanLogExportUnlock";

export const FRAME_DUMP_MAX = 48;
export const SAMPLE_EVERY = 12;

export type DumpedFrame = { i: number; ts: number; newIds: number[]; jpeg: string };

let entries: DumpedFrame[] = [];
let seenIds = new Set<number>();
let ingested = 0;
let lastKeptAt = -SAMPLE_EVERY;

function stripDataUrl(dataUrl: string): string {
  const comma = dataUrl.indexOf(",");
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

/**
 * Offer an ingested frame. `ids` are the dex numbers the frame read. Returns
 * true when the frame was kept.
 */
export function recordDexFrame(
  frame: { dataUrl: string; ts?: number },
  ids: readonly number[],
  enabled: boolean = isScanLogExportUnlocked(),
): boolean {
  ingested += 1;
  if (!enabled || !frame.dataUrl) return false;
  const newIds: number[] = [];
  for (const id of ids) {
    if (!seenIds.has(id)) {
      seenIds.add(id);
      newIds.push(id);
    }
  }
  const sampled = ingested - lastKeptAt >= SAMPLE_EVERY;
  if (newIds.length === 0 && !sampled) return false;
  lastKeptAt = ingested;
  entries.push({ i: ingested, ts: frame.ts ?? Date.now(), newIds, jpeg: stripDataUrl(frame.dataUrl) });
  if (entries.length > FRAME_DUMP_MAX) entries.splice(0, entries.length - FRAME_DUMP_MAX);
  return true;
}

export function dexFrameDumpCount(): number {
  return entries.length;
}

/** One `[dex-scan] frame-dump {...}` line per kept frame, oldest first. */
export function getDexFrameDumpText(): string {
  return entries
    .map((e) => `[dex-scan] frame-dump ${JSON.stringify({ i: e.i, ts: e.ts, newIds: e.newIds, jpeg: e.jpeg })}`)
    .join("\n");
}

export function clearDexFrameDump(): void {
  entries = [];
  seenIds = new Set();
  ingested = 0;
  lastKeptAt = -SAMPLE_EVERY;
}
